'use client';

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useApiMutation } from '@shared/hooks/useApiMutation';
import { useApiQuery } from '@shared/hooks/useApiQuery';

import { getUserActivity, setApplicationLike, unsetApplicationLike } from '@api/applications/general';

import type { ActivityHandle } from '@ui/uikit/components/common/Activity';
import { Activity } from '@ui/uikit/components/common/Activity';
import { FeedbackButton } from '@ui/uikit/components/common/FeedbackButton';

interface ApplicationFavoritesControlProps {
  likes?: number;
  applicationId: number;
  applicationName: string;
}

const ApplicationFavoritesControl = ({
  likes = 0,
  applicationId,
  applicationName,
}: ApplicationFavoritesControlProps) => {
  const { user, isAuth } = useAuth();

  const { open: openAuthModal } = useAuthModal();

  const { data: userActivity, isPending: isUerActivityQueryPending } = useApiQuery(
    getUserActivity,
    [applicationId],
    { enabled: isAuth },
  );

  const { mutate: setLike, isPending: isSetLikePending } = useApiMutation(
    setApplicationLike,
    applicationId,
  );

  const { mutate: unsetLike, isPending: isUnsetLikePending } = useApiMutation(
    unsetApplicationLike,
    applicationId,
  );

  const [state, setState] = useState<'on' | 'off'>(userActivity?.payload.like ? 'on' : 'off');

  const [localLikes, setLocalLikes] = useState(likes);

  useEffect(() => {
    if (!isUerActivityQueryPending && !isSetLikePending && !isUnsetLikePending) {
      setState(userActivity?.payload.like ? 'on' : 'off');
    } else {
      if (state === 'off' && isSetLikePending) {
        setState('on');
      } else {
        if (state === 'on' && isUnsetLikePending) {
          setState('off');
        }
      }
    }
  }, [
    isSetLikePending,
    isUerActivityQueryPending,
    isUnsetLikePending,
    state,
    userActivity?.payload.like,
  ]);

  const ensureAuth = useCallback(async () => {
    if (!activity.current) {
      console.warn(`Activity handle not initialized.`);

      return;
    }

    await activity.current.initiate();
  }, []);

  const activity = useRef<ActivityHandle>(null);

  const executeAction = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  const handleToggle = useCallback(
    async (state: 'on' | 'off') => {
      if (isSetLikePending || isUnsetLikePending) {
        return;
      }

      await ensureAuth();

      switch (state) {
        case 'on':
          setLocalLikes((prev) => prev + 1);

          setLike();

          return;

        case 'off':
          setLocalLikes((prev) => prev - 1);

          unsetLike();

          return;
      }
    },
    [ensureAuth, setLike, unsetLike, isSetLikePending, isUnsetLikePending],
  );

  return (
    <Fragment>
      <Activity ref={activity}>
        <Activity.Step requirement={!!user} action={executeAction} key="signin" />
      </Activity>

      <FeedbackButton
        variant="like"
        state={state}
        size="large"
        count={localLikes}
        onToggle={handleToggle}
        label={ state === 'off' ? `Add ${ applicationName ?? 'application'} to favorites` : `Remove ${ applicationName ?? 'application' } from favorites` }
      />
    </Fragment>
  );
};

export { ApplicationFavoritesControl };
